import Singleton from '../common/Singleton';
import GeometryAlgorithm from '../geometry/GeometryAlgorithm';
import MercatorTransform from '../transform/MercatorTransform';
import LineString from '../geometry/LineString';
import LineSegment from '../geometry/LineSegment';
import Util from '../common/Util';
import Point from '../geometry/Point';

/**
 * 该类是一个单例类，提供了等距投影算法和等距差分算法
 */
export default class DisProjectionAlgorithm extends Singleton {
    /**
     * 初始化构造函数.
     * @returns {undefined}
     */
    constructor() {
        super();

        this.geomAlg = GeometryAlgorithm.getInstance();
        this.mercatorTransform = new MercatorTransform();
    }

    /**
     *
     * 等距内插算法-版本1(每一节线段的长度必须符合阀值范围)
     *
     * 等距内插的原则：
     *  1.可以不保留原始点，但插值结束后，相邻两点间的距离必须严格符合阈值范围。
     *  2.所有新插的点必须在原始的线上，即必须在原始点的连线上。这最大程度上保证了插值后的形状与原始形状的相似性
     * @param coordinaties 需要进行等距内插的几何(几何的纬度必须是一致的，必须统一为二维数据或者三维数据)
     * @param val 等距的距离
     * @param minVal 最小值
     * @param maxVal 最大值
     * @return {Array}
     */
    isometricInterpolation1(coordinaties, minVal = 3.5, maxVal = 7) {
        if (!Array.isArray(coordinaties)) {
            throw new Error('传入coordinaties参数必须为数组！');
        }
        if (coordinaties.length <= 1) {
            throw new Error('传入coordinaties参数的长度必须大于2');
        }

        let dimension = 2; // 传输的数据是二维还是三维
        if (coordinaties[0].length === 3) {
            dimension = 3;
        }

        const sourcePoints = coordinaties.map(coor => ({
            type: 'Point',
            coordinates: coor
        }));

        const newCoor = [];

        newCoor[0] = sourcePoints[0]; // 将第一个点赋值给newCoor
        let last2Distance = 0.0;
        for (let i = 1; i < sourcePoints.length; i++) {
            let lastPoint = newCoor[newCoor.length - 1]; // 插值点的前一个点
            let aPoint = lastPoint; // 待插值线段的a端
            let bPoint = sourcePoints[i]; // 待插值线段的b端
            let dis = this.geomAlg.sphericalDistance(lastPoint, bPoint);

            while (i < sourcePoints.length - 1 && dis < minVal) {
                aPoint = bPoint; // 更新点a
                bPoint = sourcePoints[++i]; // 更新点b
                dis = this.geomAlg.sphericalDistance(lastPoint, bPoint);
            }

            while (dis > maxVal) {
                const newPoint = this.binarySearchPoint(lastPoint, aPoint, bPoint, minVal, maxVal, dimension);
                if (!newPoint) { // ab两点间不存在符合阈值的点
                    break;
                }
                newCoor.push(newPoint);
                lastPoint = Util.clone(newPoint); // 更新lastPoint
                aPoint = Util.clone(newPoint);
                dis = this.geomAlg.sphericalDistance(lastPoint, bPoint);
            }

            if (dis >= minVal && dis <= maxVal) {
                newCoor.push(bPoint);
            }

            if (i == sourcePoints.length - 1 && dis < minVal) { // 处理最后一个距离小于最小值(3.5)的线段
                const disLast2 = dis + last2Distance; // 最后一节线段和倒数第二节线段
                if (disLast2 < 7) {
                    newCoor.splice(newCoor.length - 1, 1); // 移除倒数第1个点
                    newCoor.push(bPoint);
                }
                if (disLast2 > 7) {
                    const midPoint = {
                        type: 'Point',
                        coordinates: []
                    };
                    midPoint.coordinates[0] = (lastPoint.coordinates[0] + bPoint.coordinates[0]) / 2;
                    midPoint.coordinates[1] = (lastPoint.coordinates[1] + bPoint.coordinates[1]) / 2;
                    if (dimension == 3) {
                        midPoint.coordinates[2] = (lastPoint.coordinates[2] + bPoint.coordinates[2]) / 2;
                    }
                    newCoor.splice(newCoor.length - 1, 1); // 移除倒数第1个点
                    newCoor.push(midPoint);
                    newCoor.push(bPoint);
                }
            }
            last2Distance = dis;
        }

        if (newCoor.length <= 1) {
            return [];
        }

        return newCoor.map(data => data.coordinates);
    }

    /**
     * 等距内插算法-版本2(间距是参数spaceBetween的值)
     *
     * 等距内插的原则：
     *  1.可以不保留原始点
     *  2：如果是新增点则新增的线段的长度必须是参数spaceBetween的值
     * @param coordinaties 需要进行等距内插的几何(几何的纬度必须是一致的，必须统一为二维数据或者三维数据)
     * @param val 等距的距离
     * @param minVal 最小值
     * @param maxVal 最大值
     * @param spaceBetween 默认间距
     * @return {Array}
     */
    isometricInterpolation(coordinaties, minVal = 3.5, maxVal = 7, spaceBetween = 5) {
        if (!Array.isArray(coordinaties)) {
            throw new Error('传入coordinaties参数必须为数组！');
        }
        if (coordinaties.length <= 1) {
            throw new Error('传入coordinaties参数的长度必须大于2');
        }

        const totalLength = this.getDistenceByCoordinates(coordinaties);
        if (totalLength < minVal) {
            return [coordinaties[0], coordinaties[coordinaties.length - 1]];
        }


        let dimension = 2; // 传输的数据是二维还是三维
        if (coordinaties[0].length === 3) {
            dimension = 3;
        }

        const sourcePoints = coordinaties.map(coor => ({
            type: 'Point',
            coordinates: coor
        }));

        const newCoor = [];
        newCoor[0] = sourcePoints[0];

        let aPoint = sourcePoints[0];
        let i = 1;
        while (i < sourcePoints.length) {
            const bPoint = sourcePoints[i];
            const dis = this.geomAlg.sphericalDistance(aPoint, bPoint);
            if (dis >= minVal && dis <= maxVal) {
                newCoor.push(bPoint);
                aPoint = bPoint;
                i++;
            } else if (dis < minVal) {
                if (i === sourcePoints.length - 1) {
                    const lastDis = this.geomAlg.sphericalDistance(newCoor[newCoor.length - 2], bPoint); // 计算newCoor中的最后一个点和bPoint的距离
                    if (lastDis > maxVal) {
                        const middelPoint = this.getMiddlePoint(newCoor[newCoor.length - 2], bPoint); // 最后一截线段的长度小于3.5米的时候，将最后两节合并然后平均分
                        newCoor[newCoor.length - 1] = middelPoint;
                    } else {
                        newCoor.pop(); // 删除数组的最后一位
                    }
                    newCoor.push(bPoint);
                }
                i++;
            } else {
                const newPoint = this.getPointByDisInLineSegment(aPoint, bPoint, spaceBetween);
                newCoor.push(newPoint);
                aPoint = newPoint;
            }
        }

        if (newCoor.length <= 1) {
            return [];
        }

        return newCoor.map(data => data.coordinates);
    }


    /**
     * 使用二分查找插值点
     * @param point 插值点的前一个点
     * example
     * *-----------*--------*
     * point------left-----right
     * @param left 左边点
     * @param right 右边点
     * @param minVal 最小值
     * @param maxval 最大值
     * @param dimension 纬度 2-经度纬度 3-经度纬度和高程
     */
    binarySearchPoint(point, leftPoint, rightPoint, minVal, maxval, dimension) {
        let left = leftPoint;
        let right = rightPoint;
        let insertPoint = null;
        for (let i = 0; i < 20; i++) { // 如果二分查找20次仍没找到，放弃查找
            const mid = {
                type: 'Point',
                coordinates: []
            };

            mid.coordinates[0] = (left.coordinates[0] + right.coordinates[0]) / 2;
            mid.coordinates[1] = (left.coordinates[1] + right.coordinates[1]) / 2;
            if (dimension == 3) {
                mid.coordinates[2] = (left.coordinates[2] + right.coordinates[2]) / 2;
            }

            const dis = this.geomAlg.sphericalDistance(mid, point);
            if (dis >= minVal && dis <= maxval) {
                insertPoint = mid;
                break;
            }
            if (dis < minVal) {
                left = mid;
            } else {
                right = mid;
            }
        }

        return insertPoint;
    }

    /**
     * 根据传入的坐标计算，计算坐标点的总距离
     * @param coordinates
     */
    getDistenceByCoordinates(coordinates) {
        const disArr = [];
        for (let i = 0; i < coordinates.length - 1; i++) {
            const dis = this.geomAlg.sphericalDistance({
                type: 'Point',
                coordinates: coordinates[i]
            }, {
                type: 'Point',
                coordinates: coordinates[i + 1]
            });
            disArr.push(dis);
        }

        let dis = 0;

        disArr.forEach(distance => {
            dis += distance;
        });

        return dis;
    }

    /**
     * 等距投影算
     *      P
     *     -  -
     *    -     -
     *   -        -
     *  A*B**C**D***E
     * @param coordinates {Array} 线段A-B-C-D-E的坐标（三维）
     * @param index {number} c点的下标
     * @param pointCoor  {Array} 点的坐标(三维)
     * @author wuzhen
     * @data 20200814
     * @return {Array} 返回的是A-P-E的坐标(三维,并且会将B-D点分别投影到A-P上和P-E上)
     * @example
     * equalRatioProjection(
     *     [[117.3307858, 40.6370307,10],[117.3308408 40.6370311,10],[117.3309065 40.6370315,10],[117.3310164 40.6370322,10],[117.3310754 40.6370326,10],[117.3311398 40.637033,10],[117.3312511 40.6370337,10],[117.3313383 40.6370343,10], [117.3314135 40.6370348,10]],
     *     3,
     *     [117.3311372, 40.6371884]
     * )
     */
    equalRatioProjection(coordinates, index, pointCoor) {
        const mercatorCoor = [];
        for (let i = 0; i < coordinates.length; i++) {
            const mercator = this.mercatorTransform.geographic2Mercator(coordinates[i][0], coordinates[i][1]);
            mercator[2] = coordinates[i][2];
            mercatorCoor.push(mercator);
        }

        const mercatorPointCoor = this.mercatorTransform.geographic2Mercator(pointCoor.coordinates[0], pointCoor.coordinates[1]);
        mercatorPointCoor[2] = pointCoor.coordinates[2];

        let originCoorOne = [];
        let coorOne = [];
        if (index > 0) {
            originCoorOne = mercatorCoor.slice(0, index + 1);
            coorOne = [mercatorCoor[0], mercatorPointCoor];
        }

        let originCoorTwo = [];
        let coorTwo = [];
        if (index != coordinates.length - 1) {
            originCoorTwo = mercatorCoor.slice(index, mercatorCoor.length);
            coorTwo = [mercatorPointCoor, mercatorCoor[coordinates.length - 1]];
        }

        let newCoorOne = [];
        let newCoorTwo = [];
        if (originCoorOne.length > 0) {
            newCoorOne = this.segmentationLine(originCoorOne, coorOne);
        }
        if (originCoorTwo.length > 0) {
            newCoorTwo = this.segmentationLine(originCoorTwo, coorTwo);
        }

        if (newCoorOne.length == 0) {
            return newCoorTwo;
        }

        if (newCoorTwo.length == 0) {
            return newCoorOne;
        }

        newCoorTwo.splice(0, 1); // 删除第一个点
        return this.mercatorToLatlng(newCoorOne.concat(newCoorTwo));
    }

    /**
     * 经纬度数组转化mercator
     * @param coordinates
     * @return {Array}
     */
    latlngToMercator(coordinates) {
        const returnArr = [];
        coordinates.forEach(coor => {
            const temp = this.mercatorTransform.geographic2Mercator(coor[0], coor[1]);
            temp[2] = coor[2];
            returnArr.push(temp);
        });
        return returnArr;
    }

    /**
     * mercator数组转化经纬度
     * @param coordinates
     * @return {Array}
     */
    mercatorToLatlng(coordinates) {
        const returnArr = [];
        coordinates.forEach(coor => {
            const temp = this.mercatorTransform.mercator2Geographic(coor[0], coor[1]);
            temp[2] = coor[2];
            returnArr.push(temp);
        });
        return returnArr;
    }

    /**
     * 将线段2按照线段1的形状点进行等比分割
     * @param coor1 线段1的几何(此参数必须为三维坐标)
     * @param coor2 线段2的几何(此参数必须为三维坐标)
     * @param isConvert 是否需要将coordinates1和coordinates1转换为mercator投影后的坐标
     */
    segmentationLine(coor1, coor2, isConvert = false) {
        let coordinates1 = coor1;
        let coordinates2 = coor2;
        if (isConvert) {
            coordinates1 = this.latlngToMercator(coor1);
            coordinates2 = this.latlngToMercator(coor2);
        }

        const line1 = new LineString();
        line1.coordinates = [];
        coordinates1.forEach(coor => line1.coordinates.push(new Point(coor[0], coor[1])));
        const line1Length = line1.length();

        const line2 = new LineString();
        line2.coordinates = [];
        coordinates2.forEach(coor => line2.coordinates.push(new Point(coor[0], coor[1])));
        const line2Length = line2.length();

        const tempLine = new LineString();
        tempLine.coordinates = [];
        tempLine.coordinates.push(new Point(coordinates1[0][0], coordinates1[0][1]));

        let updateIndex = null;

        if (coor2.length > coor1.length) {
            updateIndex = [];
        }

        for (let i = 1; i < coordinates1.length - 1; i++) {
            tempLine.coordinates.push(new Point(coordinates1[i][0], coordinates1[i][1]));
            const tempLength = tempLine.length();
            const newPoint = line2.getPointByLength(tempLength / line1Length * line2Length);

            const pType = newPoint[0];
            const preIndex = newPoint[1];
            const point = newPoint[3];
            if (pType == 'betweenVertex') {
                line2.coordinates.splice(preIndex + 1, 0, point);
                coordinates2.splice(preIndex + 1, 0, [point.x, point.y, coordinates1[i][2]]); // 增加一个点
            } else {
                coordinates2[preIndex + 1][2] = coordinates1[i][2]; // 修改点的Z值
            }
            if (updateIndex) {
                updateIndex.push(preIndex + 1);
            }
        }

        if (updateIndex) {
            this.calculateZ(updateIndex, coordinates2);
        }

        if (isConvert) {
            return this.mercatorToLatlng(coordinates2);
        }

        return coordinates2;
    }

    /**
     * 根据下标索引计算z值
     * @param updateIndex 索引数组
     * @param coordinates2 坐标值
     */
    calculateZ(updateIndex, coordinates2) {
        const tempZ = function (start, end, coor) {
            const step = Math.abs(end - start);
            if (step > 1) {
                const dis = coor[end][2] - coor[start][2];
                const disStep = dis / step;

                for (let i = start; i < end; i++) {
                    coor[i + 1][2] = coor[i][2] + disStep;
                }
            }
        };

        for (let i = 0; i < updateIndex.length; i++) {
            if (i == 0) {
                tempZ(0, updateIndex[i], coordinates2);
            } else if (i === coordinates2.length - 1) {
                tempZ(updateIndex[i], coordinates2.length(), coordinates2);
            } else {
                tempZ(updateIndex[i], updateIndex[i + 1], coordinates2);
            }
        }
    }

    /**
     * 获取两个点中间的点
     * @param sPoint 起点经纬度坐标
     * @param ePoint 终点经纬度坐标
     */
    getMiddlePoint(sPoint, ePoint) {
        const length = this.geomAlg.sphericalDistance(sPoint, ePoint); // 球面距离
        return this.getPointByDisInLineSegment(sPoint, ePoint, length / 2);
    }

    /**
     * 从线段上获取指定距离的点
     * @param sPoint 起点经纬度坐标
     * @param ePoint 终点经纬度坐标
     * @param dis 指定的距离,单位是米
     */
    getPointByDisInLineSegment(sPoint, ePoint, dis) {
        const sTemp = this.mercatorTransform.geographic2Mercator(sPoint.coordinates[0], sPoint.coordinates[1]);
        const eTemp = this.mercatorTransform.geographic2Mercator(ePoint.coordinates[0], ePoint.coordinates[1]);
        const sp = new Point(sTemp[0], sTemp[1]);
        const ep = new Point(eTemp[0], eTemp[1]);
        const lineSegment = new LineSegment(sp, ep);
        const length = lineSegment.length(); // 平面墨卡托距离
        const sphericalDistance = this.geomAlg.sphericalDistance(sPoint, ePoint); // 球面距离
        const sDis = length / sphericalDistance * dis; // 计算出参数dis的球面距离

        if (length <= sDis) {
            return null;
        }

        let dimension = 2; // 传输的数据是二维还是三维
        if (ePoint.coordinates.length === 3) {
            dimension = 3;
        }

        const point = lineSegment.getPointByLength(sDis);
        const pointGeo = this.mercatorTransform.mercator2Geographic(point.x, point.y);

        if (dimension === 3) {
            const sph = sPoint.coordinates[2];
            const eph = ePoint.coordinates[2];
            let ph = sph + (eph - sph) / sphericalDistance * dis;
            ph = Math.round(ph * 1000) / 1000;
            return {
                type: 'Point',
                coordinates: [pointGeo[0], pointGeo[1], ph]
            };
        }

        return {
            type: 'Point',
            coordinates: [pointGeo[0], pointGeo[1]]
        };
    }
}
